// The station window's Games and apps tab, as pure rules (F-CAT-03, F-CAT-09, ADR-0056; spec/13 §3.3): the one status each
// title wears, whether it needs attention, and the filter chips. No I/O, so the tab's words are unit-tested without a render.

import type { StationLibraryTitleState } from '@clavian/api-client';

/** The one status a title row wears; each is a short pill (copy: `library.players.pill.<key>`). */
export type AppStatusKey =
  | 'installed'
  | 'unverified'
  | 'updating'
  | 'updatePaused'
  | 'installing'
  | 'queued'
  | 'failed'
  | 'notHere'
  | 'otherAccount'
  | 'missingFromImage';

export interface AppStatus {
  readonly key: AppStatusKey;
  readonly tone: 'ok' | 'warn' | 'danger' | 'neutral' | 'info';
  /** The update's progress, for `updating` and `updatePaused`. */
  readonly percent?: number;
  /** Needs a person: failed, not on this PC, installed for another account only, missing from the image. */
  readonly attention: boolean;
}

export type AppFilter = 'all' | 'updating' | 'attention';

/** What the PC said about an app for players (F-CAT-09) as a status; the library entry's PC list wears the same pills. */
export function statusOfState(state: 'installed' | 'installing' | 'queued' | 'failed' | 'other_account_only' | 'missing_from_image' | 'not_on_this_pc'): AppStatus {
  switch (state) {
    case 'installed':
      return { key: 'installed', tone: 'ok', attention: false };
    case 'installing':
      return { key: 'installing', tone: 'info', attention: false };
    case 'queued':
      return { key: 'queued', tone: 'neutral', attention: false };
    case 'failed':
      return { key: 'failed', tone: 'danger', attention: true };
    case 'other_account_only':
      return { key: 'otherAccount', tone: 'warn', attention: true };
    case 'missing_from_image':
      return { key: 'missingFromImage', tone: 'warn', attention: true };
    case 'not_on_this_pc':
      return { key: 'notHere', tone: 'neutral', attention: true };
  }
}

/**
 * The status of one title on one PC. An update shows its percent while the PC is online; offline it is "paused at N%" rather
 * than a live-looking number. Otherwise what the PC said about the app for players (F-CAT-09) wins over plain detection.
 */
export function appStatusOf(title: StationLibraryTitleState, online: boolean): AppStatus {
  if (title.updating !== null) {
    const percent = Math.round(title.updating.progress);
    return online ? { key: 'updating', tone: 'warn', percent, attention: false } : { key: 'updatePaused', tone: 'neutral', percent, attention: false };
  }
  // `installed` is left to detection below, which also knows an unverified title.
  if (title.provisioning !== null && title.provisioning.state !== 'installed') return statusOfState(title.provisioning.state);
  if (title.installed) return title.unverified ? { key: 'unverified', tone: 'neutral', attention: false } : { key: 'installed', tone: 'ok', attention: false };
  return { key: 'notHere', tone: 'neutral', attention: true };
}

/** Whether a status belongs under a filter chip. */
export function inFilter(status: AppStatus, filter: AppFilter): boolean {
  if (filter === 'updating') return status.key === 'updating' || status.key === 'updatePaused';
  if (filter === 'attention') return status.attention;
  return true;
}

/** The counts on the chips. */
export function filterCounts(statuses: readonly AppStatus[]): Record<AppFilter, number> {
  return { all: statuses.length, updating: statuses.filter((s) => inFilter(s, 'updating')).length, attention: statuses.filter((s) => s.attention).length };
}
